/**
 * drop-gate.js
 * Countdown that replaces the buy button until a product's drop time.
 * Paired with snippets/drop-gate.liquid.
 *
 * The server decides whether a product is locked (Liquid compares the
 * product's custom.drop_launch_at metafield against the store clock).
 * This script only renders the countdown and, when it reaches zero,
 * reloads the page so the server can re-render it unlocked.
 */
class DropGate extends HTMLElement {
  connectedCallback() {
    this.launchMs = parseInt(this.dataset.launchMs || '0', 10);
    this.productUrl = this.dataset.productUrl || window.location.pathname;

    this.units = {
      days: this.querySelector('[data-drop-unit="days"]'),
      hours: this.querySelector('[data-drop-unit="hours"]'),
      minutes: this.querySelector('[data-drop-unit="minutes"]'),
      seconds: this.querySelector('[data-drop-unit="seconds"]'),
    };
    this.whenEl = this.querySelector('[data-drop-when]');
    this.statusEl = this.querySelector('[data-drop-status]');

    if (!this.launchMs) return;

    this.renderLocalTime();
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  /**
   * Show the launch time in the visitor's own timezone, so nobody has to
   * do EST-to-wherever math in their head.
   */
  renderLocalTime() {
    if (!this.whenEl) return;
    try {
      const when = new Date(this.launchMs);
      this.whenEl.textContent = when.toLocaleString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch (e) {
      /* keep the Liquid-rendered fallback already in the markup */
    }
  }

  tick() {
    const remaining = this.launchMs - Date.now();

    if (remaining <= 0) {
      this.release();
      return;
    }

    // We're genuinely still counting down, so any reload attempts recorded
    // from a previous drop are stale. Clear them once.
    if (!this.counterCleared) {
      this.counterCleared = true;
      try {
        sessionStorage.removeItem('dropGateAttempts');
      } catch (e) {
        /* no-op */
      }
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setUnit('days', days, false);
    this.setUnit('hours', hours, days > 0);
    this.setUnit('minutes', minutes, true);
    this.setUnit('seconds', seconds, true);

    // Hide the days column entirely once we are inside 24 hours.
    if (days === 0) this.setAttribute('data-drop-within-a-day', '');
  }

  setUnit(name, value, pad) {
    const el = this.units[name];
    if (!el) return;
    el.textContent = pad ? String(value).padStart(2, '0') : String(value);
  }

  /**
   * Countdown hit zero. Reload so the server re-renders the real buy button.
   * Guarded so that a clock mismatch between the visitor and the store
   * can't put the page into a fast reload loop.
   */
  release() {
    window.clearInterval(this.timer);
    this.setAttribute('data-drop-live', '');

    if (this.releasing) return;
    this.releasing = true;

    let attempts = 0;
    try {
      attempts = parseInt(sessionStorage.getItem('dropGateAttempts') || '0', 10);
      sessionStorage.setItem('dropGateAttempts', String(attempts + 1));
    } catch (e) {
      /* private browsing - fall through with attempts = 0 */
    }

    // After ~20 tries (about 5 minutes) stop reloading and let the visitor decide.
    if (attempts > 20) {
      if (this.statusEl) {
        this.statusEl.textContent = 'This drop is live. Refresh the page to buy.';
      }
      return;
    }

    if (this.statusEl) this.statusEl.textContent = 'Live now - opening...';

    // First attempt is near-instant; later attempts back off so a stale
    // cache or a skewed clock doesn't hammer the page.
    const delay = attempts === 0 ? 800 : 15000;

    window.setTimeout(() => {
      try {
        const url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set('drop', String(Date.now()));
        window.location.href = url.toString();
      } catch (e) {
        window.location.reload();
      }
    }, delay);
  }
}

if (!customElements.get('drop-gate')) {
  customElements.define('drop-gate', DropGate);
}
